import useKickoffA2AFlow from 'hooks/useKickoffA2AFlow';
import { Map as ImmutableMap, List } from 'immutable';
import PropTypes from 'prop-types';
// CORE
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToggle } from 'react-use';

// MUI ICONS
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/DoneRounded';
import EditIcon from '@mui/icons-material/Edit';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import ExploreIcon from '@mui/icons-material/Explore';
import FlipIcon from '@mui/icons-material/Flip';
import MoneyOffRoundedIcon from '@mui/icons-material/MoneyOffRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NotInterestedRoundedIcon from '@mui/icons-material/NotInterestedRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';

// MUI
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import ExcludeFromReports from 'assets/icons/settings-menu/ExcludeFromReports.inline.svg';
import ExcludeFromSpendingPlan from 'assets/icons/settings-menu/ExcludeFromSpendingPlan.inline.svg';
import { makeStyles } from 'tss-react/mui';

import { a2aTransfersSelectors } from 'app-components/flux/a2aTransfers';
import { accountDiscoveryTypes, aggErrors } from 'app-components/flux/account-discovery';
// BIT
import { accountsActions, accountsTypes, accountsUtils } from 'app-components/flux/accounts';
import { businessSelectors } from 'app-components/flux/business';
import { actionHelpers } from 'app-components/flux/core';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { institutionLoginsSelectors, institutionLoginsTypes } from 'app-components/flux/institution-logins';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { tracker, isDemoMode, getLogger } from 'app-components/utils/core';
import * as fundingAccountActions from 'data/fundingAccounts/actions';

const log = getLogger('AccountListItem');
// DATA
import * as fundingAccountSelectors from 'data/fundingAccounts/selectors';

import AccountStatusChipList from 'components/Chips/AccountStatusChipList';
import { showInitiateTransferDialog, showMicrodespositDialog } from 'components/Dialogs/A2A/actions';
import { onboardA2ADialogIndexEnums } from 'components/Dialogs/A2A/onboardA2ADialogIndexEnums';
import AccountEditDialog from 'components/Dialogs/AccountEditDialog';
import { showRealEstateDialog, showVehicleDialog, VehicleDialogOrigin } from 'components/Dialogs/AssetDialog/actions';
import {
  DIALOG_TYPE as CONFIRMATION_DIALOG_TYPE,
  mkConfirmationDialogProps,
} from 'components/Dialogs/ConfirmationDialog';
// LOCAL
import Dump from 'components/Dump';
import NestedQMenu from 'components/NestedQMenu';
import { QButtonVariantEnum } from 'components/QButton';
import QTip from 'components/QuickenControls/QTip';
import BusinessIcon from 'containers/BusinessCard/BusinessIcon';

import {
  DIALOG_TYPE as DIALOG_NOTIFICATION,
  mkNotificationDialogProps,
} from 'components/Dialogs/NotificationDialog/types';
import { v4 as uuid } from 'uuid';

import styles from './styles';
import { vehicleSelectors } from 'app-components/flux/vehicle';

const useStyles = makeStyles()(styles);

const testAddFiDialogs = false;

const AccountListItem = (props) => {
  const { account, index, institutionLogin, errorBannerShowing } = props;

  const dispatch = useDispatch();
  const { classes } = useStyles();
  const theme = useTheme();
  // ====================================== Component State ================================================ //
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [showEditDialog, toggleShowEditDialog] = useToggle(false);
  const [showDebugOptions, setShowDebugOptions] = useState(false);
  const showZillowConnect = account && !account.isConnected && account.type === 'REAL_ESTATE';
  const isStripeAccount = accountsUtils.isStripeAccount(account);
  const isARAccount = account.type === 'ACCOUNTS_RECEIVABLE';

  // ============================ TESTING KEY WATCHER =========================== //
  const handleDebugKeyDown = useCallback((e) => {
    if (e.key === 'Alt') setShowDebugOptions(true);
  }, []);
  const handleDebugKeyUp = useCallback((e) => {
    if (e.key === 'Alt') setShowDebugOptions(false);
  }, []);
  useEffect(() => {
    document.addEventListener('keydown', handleDebugKeyDown);
    document.addEventListener('keyup', handleDebugKeyUp);
    return () => {
      document.removeEventListener('keydown', handleDebugKeyDown);
      document.removeEventListener('keyup', handleDebugKeyUp);
    };
  }, [handleDebugKeyDown, handleDebugKeyUp]);

  // ====================================== Redux Selectors ================================================ //
  const featureFlags = useSelector(featureFlagsSelectors.getFeatureFlags);
  const a2aTransferEnabled = useSelector(a2aTransfersSelectors.a2aTransferEnabled);
  const business = useSelector((state) =>
    account?.businessId ? businessSelectors.getBusinessForId(state, account?.businessId) : undefined,
  );
  const isTransferSetupEligible = useSelector((state) =>
    fundingAccountSelectors.isAccountEligibleForTransfers(state, account),
  );
  const isMicrodepositEligible = useSelector((state) =>
    fundingAccountSelectors.isAccountEligibleForMicrodeposits(state, account),
  );
  const isMoveMoneyEligible = useSelector((state) =>
    fundingAccountSelectors.isAccountEligibleForCashTransfer(state, account),
  );
  const isDisabled = useSelector((state) => fundingAccountSelectors.isAccountDisabled(state, account));
  const fundingAccount = useSelector((state) =>
    fundingAccountSelectors.getFundingAccountById(state, account?.fundingAccountId),
  );

  const allInstitutionsRefreshing = useSelector(institutionLoginsSelectors.getIsRefreshingAll);
  const thisInstitutionRefreshing = useSelector(institutionLoginsSelectors.getIdsRefreshing);

  const showKbbFlowOnEditAccount = useSelector((state) =>
    vehicleSelectors.selectShowKbbFlowOnEditAccount(state, account.id),
  );
  const showGenericAccountEditDialog = !showKbbFlowOnEditAccount;

  // ===================================== Redux Util Values ============================================== //
  const isError = accountsUtils.isAccountInError(account)?.isError;
  const isDisconnected = accountsUtils.isAccountDisconnected(account, institutionLogin);

  // ====================================== Callbacks ================================================ //
  const isErrorBeingFixed = () => {
    const aggStatus = account?.aggregators?.get(0)?.aggStatus;
    return (
      Boolean(institutionLogin) &&
      Boolean(thisInstitutionRefreshing.has(institutionLogin?.id)) &&
      !allInstitutionsRefreshing &&
      Boolean((aggStatus != null && aggStatus !== 'OK') || institutionLogin?.optionalAction)
    );
  };

  const dispatchQCSAccountAction = useCallback(
    (id, qcsAction) => {
      if (id && qcsAction) dispatch(accountsActions.performAccountAction({ id, qcsAction }));
    },
    [dispatch],
  );

  const handleUpdateAccount = useCallback(
    (acc, accountAction, params = {}, undoMessage) => {
      const options = { id: acc.id, ...params };
      const type = acc?.type;

      actionHelpers
        .bindPromiseAction(dispatch, accountsActions.updateAccount)(accountsTypes.mkUpdateAccount(type, options), {
          ...(undoMessage ? { undo: { userMessage: undoMessage, disableUndoAction: true } } : {}),
        })
        .then(() => {
          if (accountAction && acc?.id) {
            dispatchQCSAccountAction(acc.id, accountAction);
          }
        })
        .catch((error) => {
          dispatch(
            rootUiActions.createDialog(
              rootUiTypes.mkRootUiData({
                id: 'error-update-account-dialog',
                type: DIALOG_NOTIFICATION,
                props: ImmutableMap(
                  mkNotificationDialogProps({
                    title: 'Account update failure',
                    content: (
                      <Typography variant='titleRegular' className={classes.errorWrapper}>
                        {error.response?.data?.errors[0]?.detail ||
                          error.response?.data?.errors[0]?.title ||
                          'Sorry, there seems to be a problem on our end. Please try again later.'}
                        {error?.response?.status && (
                          <Typography
                            className={classes.errorCodeText}
                          >{`Error Code: ${error?.response?.status}`}</Typography>
                        )}
                      </Typography>
                    ),
                  }),
                ),
              }),
            ),
          );
        });
    },
    [classes.errorCodeText, classes.errorWrapper, dispatch, dispatchQCSAccountAction],
  );

  const deleteFundingAccount = useCallback(() => {
    const deleteMessage = (
      <Box display='flex' flexDirection='column'>
        <Typography variant='titleRegular'>
          This will delete the funding account connected to {account.name}? This is for devs only.
        </Typography>
        <Typography variant='titleRegular' className={classes.textSpacing}>
          Note: The fundingAccount will NOT be deleted in dwolla unless you go to dwolla and delete it. Contact Austin
          or Jabbar on how to do this.
        </Typography>
      </Box>
    );

    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'delete-funding-account',
          type: CONFIRMATION_DIALOG_TYPE,
          allowNesting: true,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              size: 'sm',
              title: 'Delete funding account',
              content: deleteMessage,
              confirmButtonVariant: QButtonVariantEnum.DELETE,
              denyButtonVariant: QButtonVariantEnum.OUTLINED,
              confirmLabel: 'Delete',
              denyLabel: 'Cancel',
              confirmAction: () => {
                dispatch(fundingAccountActions.deleteFundingAccount(fundingAccount));
              },
            }),
          ),
        }),
      ),
    );
  }, [account.name, classes.textSpacing, dispatch, fundingAccount]);

  const handleDisableTransfers = useCallback(() => {
    const disableMessage = (
      <Box display='flex' flexDirection='column'>
        <Typography variant='titleRegular'>
          Disable transfers for {account.name}? Any transfers involving this account that are already processing will
          still be completed.
        </Typography>
        <Typography variant='titleRegular' className={classes.textSpacing}>
          Note: this won’t affect existing account data or the downloading of new transactions. You’ll need to re-verify
          this account to use it for transfers in the future.
        </Typography>
      </Box>
    );

    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'disable-transfers',
          type: CONFIRMATION_DIALOG_TYPE,
          allowNesting: true,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              size: 'sm',
              title: 'Disable account transfers',
              content: disableMessage,
              confirmButtonVariant: QButtonVariantEnum.DELETE,
              denyButtonVariant: QButtonVariantEnum.OUTLINED,
              confirmLabel: 'disable',
              denyLabel: 'Cancel',
              confirmAction: () => handleUpdateAccount(account, null, { fundingAccountId: '' }, 'Transfers disabled'),
            }),
          ),
        }),
      ),
    );
  }, [account, dispatch, handleUpdateAccount, classes.textSpacing]);

  const handleRowAction = useCallback(
    (e) => {
      if (isStripeAccount) {
        kickoffEditAccount();
      } else {
        setMenuAnchorEl(e.currentTarget);
      }
    },
    [isStripeAccount, kickoffEditAccount],
  );

  const handleCloseMenu = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const updateAccountExcludeType = useCallback(
    (type) => {
      const value = !account.get(type);
      const params = { [type]: value };

      handleUpdateAccount(account, null, params);
    },
    [account, handleUpdateAccount],
  );

  const handleExcludeFromReports = useCallback(() => {
    updateAccountExcludeType('isExcludedFromReports');
    setTimeout(() => {
      handleCloseMenu();
    }, 1000);
  }, [handleCloseMenu, updateAccountExcludeType]);

  const handleExcludeFromSpendingPlan = useCallback(() => {
    updateAccountExcludeType('isExcludedFromBudgets');
    setTimeout(() => {
      handleCloseMenu();
    }, 1000);
  }, [handleCloseMenu, updateAccountExcludeType]);

  const deleteAccount = useCallback(() => {
    //  dispatch(accountsActions.deleteAccount(account));
    actionHelpers
      .bindPromiseAction(
        dispatch,
        accountsActions.deleteAccount,
      )(account)
      .then(() => {
        //  TODO: Implement official 'confirmation' dialog Ashim designed
        dispatch(
          rootUiActions.createDialog(
            rootUiTypes.mkRootUiData({
              id: 'account-deletion-confirmation-dialog',
              type: DIALOG_NOTIFICATION,
              props: ImmutableMap(
                mkNotificationDialogProps({
                  title: 'Delete account Success',
                  content: <Typography variant='titleRegular'> Your account has been successfully deleted.</Typography>,
                }),
              ),
            }),
          ),
        );
      })
      .catch((error) => {
        dispatch(
          rootUiActions.createDialog(
            rootUiTypes.mkRootUiData({
              id: 'account-deletion-failure-dialog',
              type: DIALOG_NOTIFICATION,
              props: ImmutableMap(
                mkNotificationDialogProps({
                  title: 'Account deletion failure',
                  content: (
                    <Typography variant='titleRegular' className={classes.errorWrapper}>
                      {error.response?.data?.errors[0]?.detail ||
                        error.response?.data?.errors[0]?.title ||
                        'Sorry, there seems to be a problem on our end. Please try again later.'}
                      {error?.response?.status && (
                        <Typography
                          className={classes.errorCodeText}
                        >{`Error Code: ${error?.response?.status}`}</Typography>
                      )}
                    </Typography>
                  ),
                }),
              ),
            }),
          ),
        );
      });
  }, [account, classes.errorCodeText, classes.errorWrapper, dispatch]);

  const makeAccountManual = useCallback(() => {
    if (account?.id) {
      dispatchQCSAccountAction(account.id, 'make-manual');
    } else {
      dispatch(
        rootUiActions.createDialog(
          rootUiTypes.mkRootUiData({
            id: 'make-account-manual-failure-dialog',
            type: DIALOG_NOTIFICATION,
            props: ImmutableMap(
              mkNotificationDialogProps({
                title: 'Account change failure',
                content: (
                  <Typography variant='titleRegular' className={classes.errorWrapper}>
                    The account could not be found
                  </Typography>
                ),
              }),
            ),
          }),
        ),
      );
    }
  }, [account, classes.errorWrapper, dispatch, dispatchQCSAccountAction]);

  const closeAccount = useCallback(() => {
    if (account?.id) {
      dispatchQCSAccountAction(account.id, 'close');
    } else {
      dispatch(
        rootUiActions.createDialog(
          rootUiTypes.mkRootUiData({
            id: 'close-account-failure-dialog',
            type: DIALOG_NOTIFICATION,
            props: ImmutableMap(
              mkNotificationDialogProps({
                title: 'Account change failure',
                content: (
                  <Typography variant='titleRegular' className={classes.errorWrapper}>
                    The account could not be found
                  </Typography>
                ),
              }),
            ),
          }),
        ),
      );
    }
  }, [account, classes.errorWrapper, dispatch, dispatchQCSAccountAction]);

  /* ======================================= A2A Kickoff Hook ========================================= */
  //  The logic used to determine what dialog to open, email, identity, or transfer
  const kickoffA2AFlow = useKickoffA2AFlow();

  // ====================================== Dialog Kickoffs ================================================ //
  const kickoffCloseAccount = useCallback(() => {
    handleCloseMenu();
    const closeMessage =
      account.type !== 'INVESTMENT' ? (
        <span>
          Closing {account.name} will result in the following actions: <br />
          <ul>
            <li>The account balance will be set to $0.</li>
            <li>Downloading of future transactions in Simplifi will be disabled.</li>
            <li>Note that you will still have access to view all of your past transactions.</li>
          </ul>
          Are you sure you want to proceed with closing the account?
        </span>
      ) : (
        <span>
          Closing {account.name} will result in the following actions: <br />
          <ul>
            <li>The account balance will be set to $0.</li>
            <li>All holdings associated with the account will be removed.</li>
            <li>Downloading of future transactions in Simplifi will be disabled.</li>
            <li>Note that you will still have access to view all of your past transactions.</li>
          </ul>
          Are you sure you want to proceed with closing the account?
        </span>
      );
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'close-account',
          type: CONFIRMATION_DIALOG_TYPE,
          allowNesting: true,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              title: 'Close account',
              content: closeMessage,
              confirmLabel: 'Confirm',
              denyLabel: 'Cancel',
              confirmAction: () => closeAccount(),
            }),
          ),
        }),
      ),
    );
  }, [handleCloseMenu, dispatch, account, closeAccount]);

  const kickoffMakeManual = useCallback(() => {
    handleCloseMenu();
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'make-account-manual',
          type: CONFIRMATION_DIALOG_TYPE,
          allowNesting: true,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              title: 'Make account manual',
              content: `Are you sure you want to make ${account.name} manual? This will ${
                accountsUtils.isAccountConnected(account) ? 'disconnect from your bank and' : ''
              } move the account to the manual section.`,
              confirmLabel: 'Make Manual',
              denyLabel: 'Cancel',
              confirmAction: () => makeAccountManual(),
            }),
          ),
        }),
      ),
    );
  }, [handleCloseMenu, dispatch, account, makeAccountManual]);

  const kickoffDeleteAccount = useCallback(() => {
    handleCloseMenu();
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'delete-account',
          type: CONFIRMATION_DIALOG_TYPE,
          allowNesting: true,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              title: 'Delete account',
              content: `Deleting ${account.name} will remove all of its data and transactions from Simplifi. This will not fix connection issues.`,
              confirmLabel: 'Delete',
              denyLabel: 'Cancel',
              confirmButtonVariant: QButtonVariantEnum.DELETE,
              confirmAction: () => deleteAccount(),
            }),
          ),
        }),
      ),
    );
  }, [handleCloseMenu, dispatch, account, deleteAccount]);

  const kickoffEditAccount = useCallback(() => {
    handleCloseMenu();
    if (showKbbFlowOnEditAccount && typeof account?.id === 'string') {
      dispatch(showVehicleDialog({ linkTo: account.id, dialogOrigin: VehicleDialogOrigin.EditAccount }));
    } else {
      toggleShowEditDialog();
    }
  }, [handleCloseMenu, toggleShowEditDialog, account, dispatch, showKbbFlowOnEditAccount]);

  const kickoffMicrodepositFlow = useCallback(
    (initiator) => {
      handleCloseMenu();
      dispatch(
        showMicrodespositDialog({
          fundingAccountId: fundingAccount?.id,
          hideAccountPicker: true,
          initiator,
        }),
      );
    },
    [fundingAccount, dispatch, handleCloseMenu],
  );

  const kickoffZillowConnect = useCallback(() => {
    handleCloseMenu();
    tracker.track(tracker.events.addAssetsUX_1, { userAction: 'real estate' });
    dispatch(showRealEstateDialog(account.id));
  }, [account.id, dispatch, handleCloseMenu]);

  const kickoffTransferFlow = useCallback(() => {
    dispatch(
      showInitiateTransferDialog({
        initiator: 'account level details',
        transferOptions: {
          fromAccount: account,
        },
      }),
    );
  }, [account, dispatch]);

  const launchUpdateCredentialsDialog = useCallback(() => {
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: uuid(),
          type: accountDiscoveryTypes.DIALOG_TYPE_ACCOUNTS_DISCOVERY,
          props: ImmutableMap(
            accountDiscoveryTypes.mkAccountDiscoveryDialogProps({
              mode: 'UPDATE-CREDENTIALS',
              institutionLogin,
            }),
          ),
        }),
      ),
    );
  }, [dispatch, institutionLogin]);

  const launchMfaChallengeDialog = useCallback(() => {
    const challenges = List.of(institutionLoginsTypes.mkMfaChallengeItem({ question: 'Test Question' }));
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: uuid(),
          type: accountDiscoveryTypes.DIALOG_TYPE_ACCOUNTS_DISCOVERY,
          props: ImmutableMap(
            accountDiscoveryTypes.mkAccountDiscoveryDialogProps({
              mode: 'PROVIDE-MFA',
              institutionLogin,
              mfaChallenge: institutionLoginsTypes.mkMfaChallenge({
                institutionLoginId: institutionLogin.id,
                challenges,
              }),
            }),
          ),
        }),
      ),
    );
  }, [dispatch, institutionLogin]);

  // ====================================== Memoized Values ================================================ //
  const primaryListItemText = useMemo(
    () => (
      <div className={classes.primaryItem}>
        <Typography id={`account-${index}-name`} variant='paragraphRegular'>
          {account.name}
        </Typography>
        {account.isExcludedFromReports && account?.isExcludedFromBudgets ? (
          <QTip title='Excluded from Spending Plan & Reports'>
            <ExcludeFromSpendingPlan className={classes.imgClass} id={`account-${index}-ignored-spending-plan-label`} />
            <ExcludeFromReports className={classes.imgClass} id={`account-${index}-ignored-reports-label`} />
          </QTip>
        ) : (
          <>
            {account?.isExcludedFromBudgets && (
              <QTip title='Excluded from Spending Plan'>
                <ExcludeFromSpendingPlan
                  className={classes.imgClass}
                  id={`account-${index}-ignored-spending-plan-label`}
                />
              </QTip>
            )}
            {account.isExcludedFromReports && (
              <QTip title='Excluded from Reports'>
                <ExcludeFromReports className={classes.imgClass} id={`account-${index}-ignored-reports-label`} />
              </QTip>
            )}
          </>
        )}
      </div>
    ),
    [classes.primaryItem, account, index, classes.imgClass],
  );

  const secondaryListItemText = useMemo(
    () => (
      <Stack>
        <Typography id={`account-${index}-type`} variant='paragraphRegular' className={classes.secondaryListItem}>
          {`${
            accountsTypes.getAccountTypeNodeByQcsType(account.type, account.subType).displayName
          } ${accountsUtils?.formatFinalFour(account)}`}
        </Typography>
      </Stack>
    ),
    [index, classes, account],
  );
  const secondaryTypographyProps = useMemo(() => ({ component: 'div' }), []);

  // ======================================= Account Status ====================================== //
  const accountStatus = {
    eligible: {
      showChip: a2aTransferEnabled && isTransferSetupEligible?.status, // "showChip" is used to track the status of the account
      enableTransferFunctions: a2aTransferEnabled && isTransferSetupEligible?.isEligible,
    },
    ineligible: {
      showChip: false,
      enableTransferFunctions: a2aTransferEnabled && !isTransferSetupEligible?.isEligible,
    },
    depositPending: {
      showChip: a2aTransferEnabled && isMicrodepositEligible?.status,
      enableTransferFunctions: a2aTransferEnabled && isMicrodepositEligible?.isEligible,
      manualVerification: a2aTransferEnabled && isMicrodepositEligible?.isManualVerified,
    },
    verified: {
      showChip: a2aTransferEnabled && isMoveMoneyEligible?.status,
      enableTransferFunctions: a2aTransferEnabled && isMoveMoneyEligible?.isEligible,
    },
    fundingAccountFailure: {
      showChip: a2aTransferEnabled && isDisabled?.status,
    },
    error: {
      showChip: isError,
    },
    closed: {
      showChip: account?.isClosed,
    },
    disconnected: {
      showChip: isDisconnected && !account?.isClosed && !isDemoMode(),
    },
  };

  let ineligibleText = '';
  if (accountStatus.depositPending.showChip && !accountStatus.depositPending.enableTransferFunctions) {
    ineligibleText = isMicrodepositEligible?.reasonString;
  } else if (accountStatus.verified.showChip && !accountStatus.verified.enableTransferFunctions) {
    ineligibleText = isMoveMoneyEligible?.reasonString;
  } else if (accountStatus.fundingAccountFailure.showChip) {
    ineligibleText = isDisabled.reasonString;
  } else {
    ineligibleText = isTransferSetupEligible?.reasonString;
  }

  // ============================================================================================ //

  const menuOptions = () => {
    const retOptions = [];
    if (institutionLogin?.channel !== 'APPLE_WALLET' && accountStatus.error.showChip && !errorBannerShowing) {
      retOptions.push({
        label: 'Fix error',
        onSelect: () => {
          if (!isErrorBeingFixed()) {
            const aggregation = account?.aggregators?.get(0);
            const aggAction = aggErrors.getQcsAggregationAction(
              institutionLogin,
              aggregation?.aggStatus,
              aggregation?.aggStatusCode,
              featureFlags,
            );
            if (aggAction) {
              aggErrors.handleAggAction(dispatch, aggAction, 'settings account row');
            } else {
              log.error(
                'No aggAction available for Fix Error menu option',
                '\nAccount ID:',
                account?.id,
                '\nAccount:',
                account?.name,
                '\nInstitution Login ID:',
                institutionLogin?.id,
                '\nInstitution:',
                institutionLogin?.name,
                '\nAgg Status:',
                aggregation?.aggStatus,
                '\nAgg Status Code:',
                aggregation?.aggStatusCode,
              );
            }
            handleCloseMenu();
          }
        },
        id: 'fix-error-btn',
        startIcon: <ErrorRoundedIcon />,
        divider: true,
        disabled: isErrorBeingFixed(),
      });
    }

    retOptions.push({
      label: 'Edit account',
      onSelect: kickoffEditAccount,
      id: 'edit-account',
      startIcon: <EditIcon />,
      divider: true,
    });

    // Wrapped them all in a feature flag
    if (a2aTransferEnabled) {
      if (accountStatus.eligible.enableTransferFunctions) {
        retOptions.push({
          label: 'Set up account transfers',
          onSelect: () => {
            handleCloseMenu();
            kickoffA2AFlow({
              initiator: 'account level details',
              startTabIndex: onboardA2ADialogIndexEnums.ACCT_INFO,
              initialInstitutionId: institutionLogin?.institutionId,
              initialAccountId: account?.id,
            });
          },
          id: 'setup-transfer-account',
          startIcon: <SwapHorizRoundedIcon />,
          divider: true,
        });
      } else if (accountStatus.depositPending.enableTransferFunctions) {
        retOptions.push({
          label: 'Verify account for transfers',
          onSelect: () => {
            handleCloseMenu();
            kickoffMicrodepositFlow('account menu');
          },
          id: 'verify-deposits-account',
          startIcon: <SwapHorizRoundedIcon />,
          divider: true,
        });
      } else if (accountStatus.verified.enableTransferFunctions) {
        retOptions.push({
          label: 'Transfer money',
          onSelect: () => {
            handleCloseMenu();
            kickoffTransferFlow();
          },
          id: 'transfer-money',
          startIcon: <SwapHorizRoundedIcon />,
          divider: true,
        });
      } else {
        retOptions.push({
          label: 'Not eligible for transfers',
          id: 'setup-transfer-account',
          startIcon: <SwapHorizRoundedIcon />,
          disabled: true,
          divider: true,
          tooltip: {
            text: ineligibleText,
            placement: 'left',
          },
        });
      }

      if (
        accountStatus.verified.showChip ||
        accountStatus.depositPending.showChip ||
        accountStatus.fundingAccountFailure.showChip
      ) {
        retOptions.push({
          label: 'Disable account transfers',
          onSelect: () => {
            handleCloseMenu();
            handleDisableTransfers();
          },
          id: 'disable-transfer-money',
          startIcon: <MoneyOffRoundedIcon />,
          divider: true,
        });
      }
    }

    retOptions.push({
      isLabel: true,
      label: 'Exclude account transactions from:',
      id: 'exclusion-label',
    });

    if (featureFlags.get('reportsIgnoreExcludedAccounts')) {
      const label = 'Spending Plan';
      retOptions.push({
        customRender: (
          <div className={account.isExcludedFromBudgets ? classes.highlightedItem : classes.menuItem}>
            <ListItemIcon
              classes={{ root: account.isExcludedFromBudgets ? classes.highlightedIcon : classes.hiddenIcon }}
            >
              <CheckIcon />
            </ListItemIcon>
            <Typography className={account.isExcludedFromBudgets ? classes.highlightText : classes.text}>
              {label}
            </Typography>
            <ExcludeFromSpendingPlan className={classes.imgClass} />
          </div>
        ),
        onSelect: handleExcludeFromSpendingPlan,
        id: account?.isExcludedFromBudgets ? 'include-in-spending-plan' : 'ignore-from-spending-plan',
      });
    }

    if (featureFlags.get('ignoreAccount')) {
      const label = 'Reports';
      retOptions.push({
        customRender: (
          <div className={account.isExcludedFromReports ? classes.highlightedItem : classes.menuItem}>
            <ListItemIcon
              classes={{ root: account.isExcludedFromReports ? classes.highlightedIcon : classes.hiddenIcon }}
            >
              <CheckIcon />
            </ListItemIcon>
            <Typography className={account.isExcludedFromReports ? classes.highlightText : classes.text}>
              {label}
            </Typography>
            <ExcludeFromReports className={classes.imgClass} />
          </div>
        ),
        onSelect: handleExcludeFromReports,
        divider: !isARAccount,
        id: account.isExcludedFromReports ? 'include-in-reports' : 'ignore-from-reports',
      });
    }
    if (showZillowConnect) {
      retOptions.push({
        label: 'Connect',
        onSelect: kickoffZillowConnect,
        id: 'connect-zillow',
        startIcon: <ExploreIcon />,
        divider: true,
      });
    }
    if (institutionLogin || (account?.isConnected && account?.aggregators)) {
      retOptions.push({
        label: 'Make manual',
        onSelect: kickoffMakeManual,
        id: 'account-make-manual',
        startIcon: <FlipIcon />,
        divider: true,
      });
    }
    if (
      !account.isClosed &&
      institutionLogin?.channel !== 'APPLE_WALLET' &&
      featureFlags.get('closeAccount') &&
      !isARAccount
    ) {
      retOptions.push({
        label: 'Mark as closed',
        onSelect: kickoffCloseAccount,
        id: 'account-close',
        startIcon: <NotInterestedRoundedIcon />,
        divider: true,
      });
    }
    if (!isARAccount) {
      retOptions.push({
        customRender: (
          <Typography
            variant='bodyRegular'
            style={{ color: theme.palette.amount.negative, display: 'flex', alignItems: 'center' }}
          >
            <DeleteIcon style={{ marginRight: theme.spacing(1) }} />
            Delete account
          </Typography>
        ),
        onSelect: kickoffDeleteAccount,
        id: 'account-delete',
      });
    }
    if (process.env.NODE_ENV !== 'production' && showDebugOptions && fundingAccount) {
      retOptions.push({
        customRender: (
          <Typography
            variant='bodyRegular'
            style={{ color: theme.palette.spendingPlan.other, display: 'flex', alignItems: 'center' }}
          >
            <DeleteIcon style={{ marginRight: theme.spacing(1) }} />
            Delete funding account
          </Typography>
        ),
        onSelect: () => {
          handleCloseMenu();
          handleUpdateAccount(account, null, { fundingAccountId: '' });
          deleteFundingAccount();
        },
        id: 'delete-funding-account',
      });
    }
    retOptions.push({ nop: true, divider: testAddFiDialogs && institutionLogin });
    if (testAddFiDialogs && institutionLogin) {
      retOptions.push({
        label: 'Test Update Credentials',
        onSelect: launchUpdateCredentialsDialog,
        id: 'account-test-mfa-challenge',
        startIcon: <FlipIcon />,
      });
    }
    if (testAddFiDialogs && institutionLogin) {
      retOptions.push({
        label: 'Test MFA Challenge',
        onSelect: launchMfaChallengeDialog,
        id: 'account-test-mfa-challenge',
        startIcon: <FlipIcon />,
      });
    }

    return retOptions;
  };

  /* The spinner should only appear if specific accounts are in an error state, not if the entire FI is in an error state. Otherwise there would be too many spinners! */
  const renderErrorResolvingSpinner = () => {
    if (isErrorBeingFixed() && !errorBannerShowing) {
      return (
        <QTip solidTheme placement='top' position='top' title='Attempting to reconnect' noTippyTip arrow enableOnMobile>
          <CircularProgress size={20} className={classes.spinner} />
        </QTip>
      );
    }
    return null;
  };

  return (
    <ListItem
      dense
      secondaryAction={
        <>
          <div className={classes.rightRowContainer}>
            {renderErrorResolvingSpinner()}
            <AccountStatusChipList
              tooltipText={ineligibleText}
              hideChips={{ error: errorBannerShowing }}
              accountStatus={accountStatus}
              kickoffMicrodepositFlow={() => kickoffMicrodepositFlow('verify button')}
            />
            {business ? <BusinessIcon business={business} /> : null}
            {fundingAccount ? <Dump obj={fundingAccount} /> : null}
            <IconButton
              size='small'
              shape='rounded'
              variant='tonal'
              className={classes.threeDotButton}
              id={'account-menu-button'}
              aria-label={isStripeAccount ? 'Edit account' : 'More menu'}
              onClick={handleRowAction}
            >
              {isStripeAccount ? <EditIcon /> : <MoreVertIcon />}
            </IconButton>
            {menuAnchorEl ? (
              <NestedQMenu
                width={305}
                id={'account-menu'}
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleCloseMenu}
                options={menuOptions()}
              />
            ) : null}
          </div>
          {showGenericAccountEditDialog && (
            <AccountEditDialog
              key={showEditDialog}
              open={showEditDialog}
              onClose={() => (showEditDialog ? toggleShowEditDialog() : null)}
              accountId={account.id}
              maxWidth='sm'
              fullWidth
            />
          )}
        </>
      }
    >
      <Dump obj={account} />
      <ListItemText
        primary={primaryListItemText}
        secondary={secondaryListItemText}
        secondaryTypographyProps={secondaryTypographyProps}
      />
    </ListItem>
  );
};
AccountListItem.propTypes = {
  account: PropTypes.object,
  index: PropTypes.number,
  institutionLogin: PropTypes.object,
  errorBannerShowing: PropTypes.bool,
};

export default AccountListItem;
